<template>
    <card-skeleton class-name="w-100 bg-transparent pa-0 overflow-visible" :is-loading="isLoading">
        <template v-if="issueCommentCount">
            <div v-for="comment in issueComments" :key="comment.id" class="d-flex bg-transparent mb-8 pa-0">
                <div :class="comment.isResponse ? 'ml-4 align-self-center order-2' : 'mr-4 align-self-center'">
                    <svg-icon :icon="getIcon(comment.isResponse)" alt="logo" />
                </div>
                <v-card-item
                    size="large"
                    :class="[
                        'flex-1-0-0 rounded-lg ma-0 pa-1 px-4 py-3 bg-white border-primary-light border-opacity-100 elevation-2',
                        comment.isResponse ? 'border-e-lg' : 'border-s-lg'
                    ]"
                >
                    <span class="font-weight-regular text-size-xs">
                        {{ formatTimestamp(comment.createdAt, 'dd.MM.yyyy HH:mm') }}
                    </span>
                    <v-card-title class="text-h6">
                        {{ comment.subject }}
                    </v-card-title>
                    <v-card-text class="px-0 py-2 text-size-xs font-weight-regular">
                        <span v-if="comment.isResponse">
                            {{ comment.body }}
                        </span>
                        <span v-else v-html="comment.body" />
                    </v-card-text>
                    <v-card-item v-if="comment.attachments?.length" class="pl-0 pt-0">
                        <h4 class="text-h6 mb-2">{{ t('inquiry.attachments') }}:</h4>
                        <div class="d-flex flex-wrap gap-2">
                            <template v-if="id">
                                <issue-details-single-file
                                    v-for="attachment in comment.attachments"
                                    :key="attachment.id"
                                    :file-name="attachment.fileName"
                                    @click="generateAttachmentUrl(id, attachment.id, attachment.fileName)"
                                />
                            </template>
                        </div>
                    </v-card-item>
                </v-card-item>
            </div>
        </template>
        <div v-else class="text-subtitle-1 text-medium-emphasis">
            {{ t('inquiry.noComments') }}
        </div>
    </card-skeleton>
    <notification-bar
        v-model:show="notificationBarState.show"
        :color="notificationBarState.color"
        :icon="notificationBarState.icon"
        :border="notificationBarState.border"
        :message="notificationBarState.message"
    />
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { formatTimestamp } from '@/helpers';
import { IIssueDetailsComments } from '../../types';
import CardSkeleton from '@/features/common/components/CardSkeleton.vue';
import SvgIcon from '@/features/common/components/SvgIcon.vue';
import round from '@/assets/round_logo.svg';
import user from '@/assets/user.svg';
import { NotificationBar } from '@/features/common/components/form';
import { useFormData, useNotification } from '@/features/common/components/form/composables';
import IssueDetailsSingleFile from '@/features/issuesPage/components/Details/IssueDetailsSingleFile.vue';
const { downloadAttachment } = useFormData();

const { t } = useI18n();
const { notificationBarState, notificationSuccess, notificationError } = useNotification();
const getIcon = (isResponse: boolean): string => <string>(isResponse ? user : round);

const generateAttachmentUrl = async (issueId: number, attachmentId: string, fileName: string): Promise<void> => {
    const getAttachment = await downloadAttachment(issueId, attachmentId, fileName);
    if (getAttachment) {
        notificationBarState.value = notificationSuccess(`${t('inquiry.attachmentDownloaded')} - ${fileName}`);
    } else {
        notificationBarState.value = notificationError(t('inquiry.attachmentFailed'));
    }
};

defineProps<IIssueDetailsComments>();
</script>
